import { useEffect, useRef, useState } from 'react';

import { Icon } from '@/new/components/icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { TextWithReferences } from '@/new/components/text-references';
import { cn } from '@/new/lib/utils';
import { Summary as IssueSummary } from '@/types';

function observeElementAndChildren(el: HTMLElement, onResize: () => void) {
  const resizeObserver = new ResizeObserver(onResize);

  const observeTargets = () => {
    resizeObserver.disconnect();
    resizeObserver.observe(el);
    for (const child of Array.from(el.children)) resizeObserver.observe(child);
    onResize();
  };

  observeTargets();

  const mutationObserver = new MutationObserver(observeTargets);
  mutationObserver.observe(el, { childList: true, subtree: true });

  return () => {
    mutationObserver.disconnect();
    resizeObserver.disconnect();
  };
}

// Rendered inside the Radix popover portal, so its effect runs once the content node is
// actually mounted — letting us measure overflow as soon as Radix clamps the panel height.
export function IssuePanel({ issue, onClose }: { issue: IssueSummary; onClose: () => void }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const questionsRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [openQuestion, setOpenQuestion] = useState('');
  // Height of the collapsed questions overlay, used to pad the content so its last lines
  // can scroll clear of the panel sitting on top of it.
  const [collapsedQuestionsHeight, setCollapsedQuestionsHeight] = useState(0);
  const [rootHeight, setRootHeight] = useState(0);

  const hasSubquestions = !!issue.subquestions && issue.subquestions.length > 0;
  const isQuestionOpen = openQuestion !== '';

  // The most an open answer may grow before it scrolls internally: the panel's max height
  // (the whole panel minus the top gap, 4.625rem ≈ 74px) less the question rows that stay
  // pinned above/below it. Keeping the answer within this means the panel never scrolls, so
  // every question header stays visible.
  const TOP_GAP_PX = 128;
  const answerMaxHeight = Math.max(120, rootHeight - TOP_GAP_PX - collapsedQuestionsHeight);

  // Space below the main content reserved for the collapsed accordion: its bottom offset
  // (bottom-2.5 ≈ 10px) plus a small gap so the content stops just above it.
  const QUESTIONS_GAP_PX = 10;

  // Fade out the bottom of the content while it overflows and isn't scrolled to the end.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkScroll = () => {
      setIsScrolledToBottom(el.scrollHeight - el.scrollTop <= el.clientHeight + 1);
    };

    checkScroll();
    // Observe the container and rebind child observers as async references render.
    return observeElementAndChildren(el, checkScroll);
  }, []);

  // Measure the questions overlay only while collapsed — that's the height the content
  // needs to clear. When a question is open the overlay grows to fill the panel, but the
  // content behind it is blurred so its padding no longer matters.
  const wasQuestionOpen = useRef(isQuestionOpen);
  useEffect(() => {
    const el = questionsRef.current;
    const justClosed = wasQuestionOpen.current && !isQuestionOpen;
    wasQuestionOpen.current = isQuestionOpen;
    if (!el || isQuestionOpen) return;

    // Defer measuring until after the 0.2s collapse animation when a question was just closed:
    // reading offsetHeight mid-collapse returns the still-tall open height, which would shrink
    // answerMaxHeight to its floor and clip the answer below the still-animating panel — flashing
    // the panel's white background. On first mount (no prior open) measure immediately so the
    // main content reserves space right away.
    let observer: ResizeObserver | undefined;
    const start = () => {
      // While collapsed the panel is auto-height (just the question rows), so its offsetHeight is
      // the height the main content needs to clear.
      const update = () => setCollapsedQuestionsHeight(el.offsetHeight);
      update();
      observer = new ResizeObserver(update);
      observer.observe(el);
    };

    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (justClosed) {
      timeout = setTimeout(start, 220);
    } else {
      start();
    }
    return () => {
      if (timeout) clearTimeout(timeout);
      observer?.disconnect();
    };
  }, [isQuestionOpen]);

  // Track the panel's available height so an open answer can be bounded to it.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const update = () => setRootHeight(el.clientHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={rootRef} className="relative flex h-full flex-col">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 z-10 text-gray-400 hover:text-gray-700"
        onClick={onClose}
      >
        <Icon className="lucide--x" />
      </Button>

      <div
        className={cn(
          'mx-6 mt-5 shrink-0 transition-[filter,opacity] duration-200',
          isQuestionOpen && 'pointer-events-none opacity-50 blur-[2px]',
        )}
      >
        <p className="flex items-center gap-1 text-[13px] text-gray-500">
          <Icon className="lucide--library-big mb-px text-gray-400" /> Issue
        </p>
        <h2 className="mt-0.75 flex items-center gap-2 font-serif text-2xl font-medium text-gray-900">{issue.name}</h2>
      </div>

      <div
        ref={contentRef}
        onScroll={(e) => {
          const target = e.currentTarget;
          setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
        }}
        className={cn(
          'mt-4 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto mask-b-from-80% mask-b-to-102% px-6 pb-3 text-[13px] leading-relaxed text-gray-700 transition-[mask-size,filter,opacity] duration-200',
          isQuestionOpen && 'pointer-events-none opacity-50 blur-[2px]',
        )}
        style={{
          maskSize: isScrolledToBottom ? '100% 130%' : '100% 100%',
          // Reserve room for the collapsed questions accordion so the content ends just above it
          // instead of scrolling behind it. The accordion stays absolutely positioned, so when a
          // question expands it still grows over this (now-blurred) content.
          marginBottom: hasSubquestions ? collapsedQuestionsHeight + QUESTIONS_GAP_PX : undefined,
        }}
      >
        <TextWithReferences content={issue.content} />
      </div>

      {hasSubquestions && (
        <div
          ref={questionsRef}
          // Anchored at the bottom and auto-height: the panel grows as an answer expands and
          // shrinks as it collapses. The height isn't animated directly — the Accordion animates
          // each answer's height and the panel simply follows. Answers are bounded so the panel
          // never needs to scroll, keeping every header visible.
          className="absolute inset-x-2.5 bottom-2.5 flex flex-col overflow-hidden rounded-lg border bg-white"
        >
          <Accordion
            type="single"
            collapsible
            value={openQuestion}
            onValueChange={setOpenQuestion}
            className="flex flex-col"
          >
            {issue.subquestions.map((subquestion, subIndex) => (
              <AccordionItem
                key={subIndex}
                value={String(subIndex)}
                className="flex flex-col border-b border-gray-100 last:border-b-0"
              >
                <AccordionTrigger className="px-4 py-2.5 text-[13px] font-normal text-gray-800 hover:text-blue-500 [&>span]:line-clamp-1 [&[data-state=open]>span]:line-clamp-none">
                  <span className="min-w-0 pr-1">{subquestion.question}</span>
                </AccordionTrigger>
                <AccordionContent>
                  {/* Bound + scroll the answer here (not on AccordionContent, whose outer element
                      is overflow-hidden) so the panel itself never scrolls and every header stays visible. */}
                  <div
                    className="space-y-3 overflow-y-auto px-4 pb-4 text-[13px] leading-relaxed text-gray-700"
                    style={{ maxHeight: answerMaxHeight }}
                  >
                    <TextWithReferences content={subquestion.answer} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
